import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiShield, FiDatabase, FiLock, FiMail, FiAlertCircle, FiUser, FiEye } = FiIcons;

const Privacy = () => {
  return (
    <section className="pt-32 pb-20 px-6 min-h-screen bg-gradient-to-br from-rose-light via-white to-rose-accent/20">
      <div className="container mx-auto max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <div className="flex items-center justify-center space-x-2 mb-6">
            <div className="w-12 h-12 bg-gradient-to-br from-rose-primary to-rose-secondary rounded-xl flex items-center justify-center">
              <SafeIcon icon={FiShield} className="text-white text-xl" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold">
              <span className="bg-gradient-to-r from-rose-primary to-rose-secondary bg-clip-text text-transparent">
                Privacy Policy
              </span>
            </h1>
          </div>
          <p className="text-gray-600 text-lg">
            Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="bg-white rounded-2xl shadow-xl p-8 md:p-12"
        >
          <div className="prose prose-lg max-w-none">
            <div className="space-y-8">
              {/* Introduction */}
              <div>
                <p className="text-gray-600 leading-relaxed text-lg">
                  Gifter's Choice ("we", "our", or "us") respects your privacy and is committed to protecting your personal information. This Privacy Policy explains how we collect, use, and safeguard your information when you use our mobile application.
                </p>
              </div>

              {/* Information We Collect */}
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                  <SafeIcon icon={FiDatabase} className="text-rose-primary mr-3" />
                  1. Information We Collect
                </h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  We collect the following types of information:
                </p>
                <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
                  <li>Email address and password (for account creation)</li>
                  <li>Name and optional birthday</li>
                  <li>Gift list items you create (title, price, links, notes, images)</li>
                  <li>Device contacts (only if you grant permission to find friends)</li>
                  <li>Usage data and analytics</li>
                </ul>
              </div>

              {/* How We Use Your Information */}
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                  <SafeIcon icon={FiUser} className="text-rose-primary mr-3" />
                  2. How We Use Your Information
                </h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  We use your information to:
                </p>
                <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
                  <li>Provide and maintain the Gifter's Choice service</li>
                  <li>Create and manage your account</li>
                  <li>Enable sharing of your gift lists with friends and family</li>
                  <li>Help you discover friends using the app</li>
                  <li>Improve our services and user experience</li>
                  <li>Send important service updates</li>
                </ul>
              </div>

              {/* Information Sharing */}
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                  <SafeIcon icon={FiEye} className="text-rose-primary mr-3" />
                  3. Information Sharing
                </h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  We do not sell your personal information. We only share your information:
                </p>
                <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
                  <li>With people you choose to share your gift list with</li>
                  <li>When required by law</li>
                  <li>With service providers who help operate our app (with strict confidentiality agreements)</li>
                </ul>
              </div>

              {/* Data Security */}
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                  <SafeIcon icon={FiLock} className="text-rose-primary mr-3" />
                  4. Data Security
                </h2>
                <p className="text-gray-600 leading-relaxed">
                  We implement industry-standard security measures to protect your personal information, including encryption of passwords and secure data transmission. However, no method of transmission over the internet is 100% secure.
                </p>
              </div>

              {/* Your Privacy Controls */}
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                  <SafeIcon icon={FiUser} className="text-rose-primary mr-3" />
                  5. Your Privacy Controls
                </h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  You can:
                </p>
                <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
                  <li>Make your gift list public or private</li>
                  <li>Delete items from your list at any time</li>
                  <li>Delete your account and all associated data</li>
                  <li>Opt out of contact syncing</li>
                </ul>
              </div>

              {/* Children's Privacy */}
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                  <SafeIcon icon={FiShield} className="text-rose-primary mr-3" />
                  6. Children's Privacy
                </h2>
                <p className="text-gray-600 leading-relaxed">
                  Gifter's Choice is not intended for children under 13. We do not knowingly collect personal information from children under 13.
                </p>
              </div>

              {/* Changes to This Policy */}
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                  <SafeIcon icon={FiAlertCircle} className="text-rose-primary mr-3" />
                  7. Changes to This Policy
                </h2>
                <p className="text-gray-600 leading-relaxed">
                  We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date.
                </p>
              </div>

              {/* Contact Us */}
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                  <SafeIcon icon={FiMail} className="text-rose-primary mr-3" />
                  8. Contact Us
                </h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  If you have questions about this Privacy Policy, please contact us at:
                </p>
                <div className="mt-4 p-4 bg-rose-light rounded-lg">
                  <p className="text-gray-700">
                    <strong>Email:</strong> support@gifterschoice.com
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Privacy;